import { PhotoManager } from "./photo-manager.js";
import { consoleMsg } from "../shared/utility.js";
import { mkAbs } from "../shared/page-init.js";

const app = window.app;

export class InspectionPhotoManager extends PhotoManager {
	constructor(name) {
		super(name);
	}

	attachPhotoEventListeners(context = document) {
		const photoCols = context.querySelectorAll("ion-col");
		photoCols.forEach((col) => {
			col.addEventListener("click", async () => {
				const img = col.querySelector(".photo-container img");
				const key = img?.getAttribute("name");
				if (!key) return;

				// Desktop has no camera app, so both sheet options would do the same thing
				if (!PhotoManager.supportsCameraCapture()) {
					await this.pickPhoto(key);
					return;
				}

				const actionSheet = document.createElement("ion-action-sheet");
				actionSheet.header = col.querySelector(".photo-title")?.textContent.trim();
				actionSheet.buttons = [
					{
						text: "Take Photo",
						icon: "camera",
						handler: async () => {
							consoleMsg(`Taking photo for key ${key}`);
							await this.takePhoto(key);
						},
					},
					{
						text: "Choose from Gallery",
						icon: "image",
						handler: async () => {
							consoleMsg(`Choosing gallery photo for key ${key}`);
							await this.pickPhoto(key);
						},
					},
					{
						text: "Cancel",
						role: "cancel",
						icon: "close",
					},
				];

				document.body.appendChild(actionSheet);
				await actionSheet.present();
			});
		});
	}

	async takePhoto(key) {
		await this.addPhoto(key, "camera-input");
	}

	async pickPhoto(key) {
		await this.addPhoto(key, "gallery-input");
	}

	async addPhoto(key, inputId) {
		try {
			const file = await PhotoManager.captureViaFileInput(inputId);
			const webPath = URL.createObjectURL(file);

			this.savePhoto(key, webPath);
			this.updatePhotoUI();
		} catch (error) {
			consoleMsg("Error adding photo: ", error);
		}
	}

	async savePhoto(key, img) {
		consoleMsg("INSPECTION PHOTO MANAGER SAVE PHOTO METHOD CALLED...");
		key = String(key).replace(/\.png$/i, "");
		const record = app.state.photoData[key];
		record.src = img;
		record.updated = true;
		this.savePhotoData();

		const element = document.getElementById(key);
		const image = element.querySelector(`img[name="${key}"]`);
		image.src = "";

		setTimeout(async () => {
			image.src = app.state.photoData[key].src;
			await this.constructor.storePicDb(image, null, null, key);

			// The tile was showing the raw camera file; repaint it from what was actually
			// stored so the preview matches the rotated, cropped image in the report
			const stored = await store.get(`${app.state.storageKey}_${key}`);
			if (stored?.photo) {
				if (record.src?.startsWith("blob:")) URL.revokeObjectURL(record.src);
				record.src = URL.createObjectURL(PhotoManager.base64ToBlob(stored.photo, "image/jpeg"));
				image.src = record.src;
			}

			const icon = document.getElementById(key + "-valid-icon");
			const div = document.getElementById(key);
			const errorDiv = document.getElementById(key + "-error");
			icon.classList.remove("hidden");
			div.classList.remove("hidden");
			errorDiv.classList.add("hidden");
		}, 50);
	}

	async viewPhoto(photoUrl) {
		// Create the modal container
		const modal = document.createElement("div");
		modal.classList.add("photo-modal"); // Use CSS class for styling

		// Create the image element
		const image = document.createElement("img");
		image.src = photoUrl;
		image.alt = "Full-size photo";
		image.classList.add("photo-modal-image"); // Use CSS class for styling

		// Add the image to the modal
		modal.appendChild(image);

		// Add a click event to close the modal
		modal.addEventListener("click", () => {
			modal.remove();
		});

		// Append the modal to the body
		document.body.appendChild(modal);
	}

	async restoreAllPhotos() {
		const storageKey = app.state.storageKey;
		const keys = await store.keys();
		const photoKeys = keys.filter((key) => key.startsWith(`${storageKey}_car`));

		for (let key of photoKeys) {
			const stored = await store.get(key);
			const shortKey = key.slice(storageKey.length + 1); // 'car-front', 'car-odometer', etc.
			const div = document.getElementById(shortKey);
			const img = div?.querySelector("img");

			if (!stored || !stored.photo) continue;
			if (!div || !img) continue;

			app.state.photoData[shortKey].updated = true;

			const blob = PhotoManager.base64ToBlob(stored.photo, "image/jpeg");
			const freshUrl = URL.createObjectURL(blob);

			// Revoke the old one if present
			if (app.state.photoData[shortKey].src && app.state.photoData[shortKey].src.startsWith("blob:")) {
				URL.revokeObjectURL(app.state.photoData[shortKey].src);
			}

			app.state.photoData[shortKey].src = freshUrl;

			const apply = () => {
				img.setAttribute("src", freshUrl);
				img.src = freshUrl;
			};

			if (img.complete) {
				apply();
				this.hideSkeleton(div);
			} else {
				img.onload = () => {
					apply();
					this.hideSkeleton(div);
				};
				img.setAttribute("src", freshUrl);
			}
		}
	}

	updatePhotoUI() {
		const keys = Object.keys(app.state.photoData);
		consoleMsg("updatePhotoUI function called...");

		for (let i = 0; i < keys.length; i++) {
			const key = keys[i];
			const record = app.state.photoData[key];
			const element = document.getElementById(key);
			if (!element) continue;

			const image = element.querySelector("img");
			if (!image) continue;

			// Always resolve the target src
			const base = (app.settings.base || "/").replace(/\/+$/, "");
			const target = record.src;

			const rel = /^(https?:|data:|blob:)/i.test(target) ? target : base && target?.startsWith(base + "/") ? target.slice(base.length + 1) : target?.startsWith("/assets/") ? target.slice(1) : target;

			const resolved = mkAbs(rel);

			// Paint placeholder OR restored photo
			image.src = "";
			setTimeout(() => {
				image.setAttribute("src", resolved);
				image.src = resolved;
				this.hideSkeleton(element);
			}, 50);
		}
	}

	hideSkeleton(container) {
		const sk = container.querySelector(".photo-skeleton");
		if (sk) sk.style.display = "none";
	}

	async savePhotoData() {
		const sanitized = {};
		for (const [key, val] of Object.entries(app.state.photoData)) {
			sanitized[key] = { updated: val.updated };
		}
		await store.set("photoData", sanitized);
	}
}
